// utils
import { paramCase } from 'src/utils/change-case';
import { _id, _postTitles } from 'src/_mock/assets';

// ----------------------------------------------------------------------

const MOCK_ID = _id[1];

const MOCK_TITLE = _postTitles[2];

const ROOTS = {
  AUTH: '/auth',
  AUTH_DEMO: '/auth-demo',
  DASHBOARD: '/dashboard',
  HOME: '/',
  THREAT_INTELLIGENCE: '/threatIntelligence',
};

// ----------------------------------------------------------------------   

export const paths = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  faqs: '/faqs',
  page403: '/403',
  page404: '/404',
  page500: '/500',
  components: '/components',
  docs: 'https://docs.minimals.cc',
  changelog: 'https://docs.minimals.cc/changelog',
  zoneUI: 'https://mui.com/store/items/zone-landing-page/',
  minimalUI: 'https://mui.com/store/items/minimal-dashboard/',
  freeUI: 'https://mui.com/store/items/minimal-dashboard-free/',
  figma:
    'https://www.figma.com/file/kAYnYYdib0aQPNKZpgJT6J/%5BPreview%5D-Minimal-Web.v5.0.0?type=design&node-id=0%3A1&t=Al4jScQq97Aly0Mn-1',
  product: {
    root: `/product`,
    checkout: `/product/checkout`,
    details: (id: string) => `/product/${id}`,
    demo: {
      details: `/product/${MOCK_ID}`,
    },
  },
  post: {
    root: `/post`,
    details: (title: string) => `/post/${paramCase(title)}`,
    demo: {
      details: `/post/${paramCase(MOCK_TITLE)}`,
    },
  },
  // AUTH
  auth: {
    amplify: {
      login: `${ROOTS.AUTH}/amplify/login`,
      verify: `${ROOTS.AUTH}/amplify/verify`,
      register: `${ROOTS.AUTH}/amplify/register`,
      newPassword: `${ROOTS.AUTH}/amplify/new-password`,
      forgotPassword: `${ROOTS.AUTH}/amplify/forgot-password`,
    },
    jwt: {
      login: `${ROOTS.AUTH}/jwt/login`,
      register: `${ROOTS.AUTH}/jwt/register`,
      verify2FA: `${ROOTS.AUTH}/jwt/verify-2fa`,
    },
    firebase: {
      login: `${ROOTS.AUTH}/firebase/login`,
      verify: `${ROOTS.AUTH}/firebase/verify`,
      register: `${ROOTS.AUTH}/firebase/register`,
      forgotPassword: `${ROOTS.AUTH}/firebase/forgot-password`,
    },
    auth0: {
      login: `${ROOTS.AUTH}/auth0/login`,
    },
  },
  authDemo: {
    classic: {
      login: `${ROOTS.AUTH_DEMO}/classic/login`,
      register: `${ROOTS.AUTH_DEMO}/classic/register`,
      forgotPassword: `${ROOTS.AUTH_DEMO}/classic/forgot-password`,
      newPassword: `${ROOTS.AUTH_DEMO}/classic/new-password`,
      verify: `${ROOTS.AUTH_DEMO}/classic/verify`,
    },
    modern: {
      login: `${ROOTS.AUTH_DEMO}/modern/login`,
      register: `${ROOTS.AUTH_DEMO}/modern/register`,
      forgotPassword: `${ROOTS.AUTH_DEMO}/modern/forgot-password`,
      newPassword: `${ROOTS.AUTH_DEMO}/modern/new-password`,
      verify: `${ROOTS.AUTH_DEMO}/modern/verify`,
    },
  },

  threatIntelligence: {
    root: ROOTS.THREAT_INTELLIGENCE,
    telegramFeeds: `${ROOTS.THREAT_INTELLIGENCE}/telegram-feeds`,
    sourceManagement: `${ROOTS.THREAT_INTELLIGENCE}/source-management`,
    ransomware: {
      root: `${ROOTS.THREAT_INTELLIGENCE}/ransomware`,
      victims: `${ROOTS.THREAT_INTELLIGENCE}/ransomware/victims`,
      sectors: `${ROOTS.THREAT_INTELLIGENCE}/ransomware/sectors`,
      sectorProfile: (name: string) =>
        `${ROOTS.THREAT_INTELLIGENCE}/ransomware/sectors/${encodeURIComponent(name)}`,
      geo: `${ROOTS.THREAT_INTELLIGENCE}/ransomware/geo`,
      countryProfile: (code: string) =>
        `${ROOTS.THREAT_INTELLIGENCE}/ransomware/geo/${encodeURIComponent(code)}`,
      groups: `${ROOTS.THREAT_INTELLIGENCE}/ransomware/groups`,
      groupProfile: (name: string) =>
        `${ROOTS.THREAT_INTELLIGENCE}/ransomware/groups/${name}`,
      graph: `${ROOTS.THREAT_INTELLIGENCE}/ransomware/graph`,
      campaigns: `${ROOTS.THREAT_INTELLIGENCE}/ransomware/campaigns`,
      threatIntel: `${ROOTS.THREAT_INTELLIGENCE}/ransomware/threat-intel`,
      cveProfile: (id: string) =>
        `${ROOTS.THREAT_INTELLIGENCE}/ransomware/vulnerabilities/${encodeURIComponent(id)}`,
      search: `${ROOTS.THREAT_INTELLIGENCE}/ransomware/search`,
      watchlist: `${ROOTS.THREAT_INTELLIGENCE}/ransomware/watchlist`,
      mitre: `${ROOTS.THREAT_INTELLIGENCE}/ransomware/mitre`,
    },
  },
  // DASHBOARD
  dashboard: {
    root: ROOTS.DASHBOARD,
    mail: `${ROOTS.DASHBOARD}/mail`,
    chat: `${ROOTS.DASHBOARD}/chat`,
    blank: `${ROOTS.DASHBOARD}/blank`,
    kanban: `${ROOTS.DASHBOARD}/kanban`,
    calendar: `${ROOTS.DASHBOARD}/calendar`,
    overviewDashboard: `${ROOTS.DASHBOARD}/overviewDashboard`,
    fileManager: `${ROOTS.DASHBOARD}/file-manager`,
    permission: `${ROOTS.DASHBOARD}/permission`,
    general: {
      app: `${ROOTS.DASHBOARD}/app`,
      company: `${ROOTS.DASHBOARD}/company`,
      intelligence: `${ROOTS.DASHBOARD}/intelligence`,
      intelligenceTherat: `${ROOTS.DASHBOARD}/intelligenceTherat`,
      takeDownRequest: `${ROOTS.DASHBOARD}/takeDownRequest`,
      intelligenceNewEditForm: (idService: string, serviceName: string) => `${ROOTS.DASHBOARD}/${idService}/${serviceName}/intelligenceNewEditForm`,
      IntelligenceView: (idService: string, companyId: string, serviceName: string) => `${ROOTS.DASHBOARD}/${idService}/${companyId}/${serviceName}/IntelligenceView`,
      intelligenceViewNewEditForm: (id: string, idService: string, companyId: string, serviceName: string) => `${ROOTS.DASHBOARD}/${id}/${idService}/${companyId}/${serviceName}/intelligenceViewNewEditForm`,
      analytics: `${ROOTS.DASHBOARD}/analytics`,
      bulkUploadPage: `${ROOTS.DASHBOARD}/bulkUploadPage`,
      banking: `${ROOTS.DASHBOARD}/banking`,
      booking: `${ROOTS.DASHBOARD}/booking`,
      file: `${ROOTS.DASHBOARD}/file`,
      generatedApiView: `${ROOTS.DASHBOARD}/generatedApiView`,
      rolesCreateView: `${ROOTS.DASHBOARD}/rolesCreateView`,
      graphCreatePage: `${ROOTS.DASHBOARD}/GraphCreatePage`,
      showDescriptionPage: (idService: string) => `${ROOTS.DASHBOARD}/${idService}/ShowDescriptionPage`,

    },
    takedownrequest: {
      root: `${ROOTS.DASHBOARD}/takeDownRequest`,
      takedownrequestview: (idService: string, companyId: string, serviceName: string) => `${ROOTS.DASHBOARD}/${idService}/${companyId}/${serviceName}/takedownrequestview`,
      takedownrequestMainPage: (idService: string, serviceName: string, idCompany: string) => `${ROOTS.DASHBOARD}/takedownrequestMainPage/${idService}/${serviceName}/${idCompany}`
    },
    companies: {
      company: `${ROOTS.DASHBOARD}/companies`,
    },
    Service: {
      ServicePage: `${ROOTS.DASHBOARD}/ServicePage`,
    },

    users: {
      users: `${ROOTS.DASHBOARD}/users`,
    },
    addusers: {
      addusers: `${ROOTS.DASHBOARD}/addusers`,
    },
    editUser: {
      editUser: (id: string) => `${ROOTS.DASHBOARD}/${id}/editUser`,
    },

    editprofile: {
      root: `${ROOTS.DASHBOARD}/editprofile`,
      editprofile: (id: string) => `${ROOTS.DASHBOARD}/editprofile/${id}/editprofile`,
    },

    user: {
      root: `${ROOTS.DASHBOARD}/user`,
      new: `${ROOTS.DASHBOARD}/user/new`,
      list: `${ROOTS.DASHBOARD}/user/list`,
      cards: `${ROOTS.DASHBOARD}/user/cards`,
      profile: `${ROOTS.DASHBOARD}/user/profile`,
      account: `${ROOTS.DASHBOARD}/user/account`,
      comapnyCreateView: (id: string) => `${ROOTS.DASHBOARD}/user/${id}/comapnyCreateView`,

      ServiceCreateView: `${ROOTS.DASHBOARD}/user/ServiceCreateView`,

      edit: (id: string) => `${ROOTS.DASHBOARD}/user/${id}/edit`,
      demo: {
        edit: `${ROOTS.DASHBOARD}/user/${MOCK_ID}/edit`,
      },
    },
    asm: {
      root: `${ROOTS.DASHBOARD}/asm`,
      assets: `${ROOTS.DASHBOARD}/asm/assets`,
      portlist: `${ROOTS.DASHBOARD}/asm/portlist`,
      reportlist: `${ROOTS.DASHBOARD}/asm/reportlist`,
      addreport:`${ROOTS.DASHBOARD}/asm/addreport`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/asm/${id}/edit`,
    },


    report: {
      root: `${ROOTS.DASHBOARD}/report`,
      vendorPerformance: `${ROOTS.DASHBOARD}/report/vendorPerformance`,
      takedownrequestlist: `${ROOTS.DASHBOARD}/report/takedownrequestlist`,
      schedulereport: `${ROOTS.DASHBOARD}/report/schedulereport`,
      detectionbymodulelist: `${ROOTS.DASHBOARD}/report/detectionbymodule`,
      modulewisebreakuplist: `${ROOTS.DASHBOARD}/report/modulewisebreakuplist`,
      tatfortakedownslist: `${ROOTS.DASHBOARD}/report/tatfortakedownslist`,
      averagetakedowntime: `${ROOTS.DASHBOARD}/report/averagetakedowntime`,
      cumulativetakedowntrend: `${ROOTS.DASHBOARD}/report/cumulativetakedowntrend`,
      socialmediadetection: `${ROOTS.DASHBOARD}/report/socialmediadetection`,
      addcompanyreport: `${ROOTS.DASHBOARD}/report/addcompanyreport`,
      darkwebmentions: `${ROOTS.DASHBOARD}/report/darkwebmentions`,

      editcompanyReport: (id: string) => `${ROOTS.DASHBOARD}/report/${id}/editcompanyReport`,

    },
    socialmediatree: {
      socialmediatree: `${ROOTS.DASHBOARD}/socialmediatree`,
    },
    userreport: {
      userreport: `${ROOTS.DASHBOARD}/userreport`,
    },

    LoginHistory:{
      loginhistory: `${ROOTS.DASHBOARD}/loginlogout`,
    },

        DarkWebMentionThreat:{
      darkwebmentionthreat: `${ROOTS.DASHBOARD}/darkwebmentionthreat`,
    },

     AsmPermission: {
      AsmPermission: `${ROOTS.DASHBOARD}/Asmpermission`,
      AsmviewList:`${ROOTS.DASHBOARD}/AsmviewList`,
      editasmpermission: (id: string) => `${ROOTS.DASHBOARD}/${id}/editasmpermission`,
    },

    product: {
      root: `${ROOTS.DASHBOARD}/product`,
      new: `${ROOTS.DASHBOARD}/product/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/product/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/product/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/product/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/product/${MOCK_ID}/edit`,
      },
    },

    invoice: {
      root: `${ROOTS.DASHBOARD}/invoice`,
      new: `${ROOTS.DASHBOARD}/invoice/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/invoice/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/invoice/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/invoice/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/invoice/${MOCK_ID}/edit`,
      },
    },
    post: {
      root: `${ROOTS.DASHBOARD}/post`,
      new: `${ROOTS.DASHBOARD}/post/new`,
      details: (title: string) => `${ROOTS.DASHBOARD}/post/${paramCase(title)}`,
      edit: (title: string) => `${ROOTS.DASHBOARD}/post/${paramCase(title)}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/post/${paramCase(MOCK_TITLE)}`,
        edit: `${ROOTS.DASHBOARD}/post/${paramCase(MOCK_TITLE)}/edit`,
      },
    },
    order: {
      root: `${ROOTS.DASHBOARD}/order`,
      details: (id: string) => `${ROOTS.DASHBOARD}/order/${id}`,
      demo: {
        details: `${ROOTS.DASHBOARD}/order/${MOCK_ID}`,
      },
    },
    job: {
      root: `${ROOTS.DASHBOARD}/job`,
      new: `${ROOTS.DASHBOARD}/job/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/job/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/job/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/job/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/job/${MOCK_ID}/edit`,
      },
    },
    tour: {
      root: `${ROOTS.DASHBOARD}/tour`,
      new: `${ROOTS.DASHBOARD}/tour/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/tour/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/tour/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/tour/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/tour/${MOCK_ID}/edit`,
      },
    },
  },
};

